import {
  normalizeInvitePermissions,
  type InvitePermissions,
  type IUser,
} from '@todesktop/shared';

/**
 * How the viewer relates to a team, in the words All apps and the account
 * menu use. Teams have no roles yet, so members are described by what their
 * permissions let them do.
 */
export const getTeamRoleLabel = ({
  isOwner,
  permissions,
  signing = false,
}: {
  isOwner: boolean;
  permissions?: InvitePermissions;
  /**
   * Count "manage signing", which only v2 has: pass the viewer's `v2Apps`
   * flag. Without it, labels read as they did before v2.
   */
  signing?: boolean;
}): string => {
  if (isOwner) return 'Owner';
  // Older invites saved no permissions on the accepted copy.
  if (!permissions) return 'Member';

  const {
    canBuild,
    canRelease,
    canManageUsers,
    canManageBilling,
    canManageSigning,
    canManageSso,
    isAdmin,
  } = normalizeInvitePermissions(permissions);
  if (isAdmin) return 'Admin';
  // Outside Admins, "manage SSO" is only ever given explicitly, by teams
  // with the `sso` flag, so it needs no flag to show.
  const abilities = [
    canBuild && 'build',
    canRelease && 'release',
    canManageUsers && 'manage members',
    canManageBilling && 'manage billing',
    signing && canManageSigning && 'manage signing',
    canManageSso && 'manage SSO',
  ].filter((ability): ability is string => Boolean(ability));

  if (abilities.length === (signing ? 5 : 4) + (canManageSso ? 1 : 0)) {
    return 'Full access';
  }
  if (!abilities.length) return 'View only';
  if (abilities.length === 1) return `Can ${abilities[0]}`;
  return `Can ${abilities.slice(0, -1).join(', ')} and ${abilities[abilities.length - 1]}`;
};

/**
 * Owners are asked once to name their team, after the change that put every
 * account's apps in a team. People without apps have nothing to recognise
 * yet, so they're asked after they make one.
 */
export const shouldShowTeamSetup = ({
  user,
  ownsApps,
}: {
  user: Pick<
    IUser,
    'isImpersonator' | 'teamSetupCompletedAt' | 'teamSetupDismissedAt'
  > | null;
  ownsApps: boolean;
}): boolean =>
  Boolean(
    user &&
    ownsApps &&
    !user.isImpersonator &&
    !user.teamSetupCompletedAt &&
    !user.teamSetupDismissedAt,
  );

export const getTeamInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((word) => word[0])
    .join('');
  return initials.toUpperCase() || '?';
};

// Dark enough for white initials to pass WCAG AA.
const TEAM_COLORS = [
  '#0f766e',
  '#9a4a0b',
  '#4338ca',
  '#b91c1c',
  '#334155',
  '#0e7490',
  '#7e22ce',
  '#15803d',
];

/** A stable colour per team, for teams without a logo. */
export const getTeamColor = (teamId: string): string => {
  let hash = 0;
  for (const char of teamId) {
    hash = (hash * 31 + char.charCodeAt(0)) % 2 ** 32;
  }
  return TEAM_COLORS[hash % TEAM_COLORS.length];
};

/**
 * What an invitation lets someone do, for the review page and inline card.
 * Every line is something they can do; there's no "can't" list to parse.
 */
export const getInviteAbilities = (
  teamName: string,
  permissions?: InvitePermissions,
  /** Include v2's signing (the viewer's `v2Apps` flag), as getTeamRoleLabel. */
  signing = false,
): string[] => {
  const {
    canBuild,
    canRelease,
    canManageUsers,
    canManageBilling,
    canManageSigning,
    canManageSso,
    isAdmin,
  } = normalizeInvitePermissions(permissions);
  if (isAdmin) {
    return [
      `See ${teamName}’s apps, builds and releases`,
      'Build and release',
      'Manage members, billing and plans',
      signing
        ? 'Change certificates, signing identities, and the team’s name and logo'
        : 'Change certificates and the team’s name and logo',
    ];
  }
  return [
    `See ${teamName}’s apps, builds and releases`,
    canBuild && 'Start builds',
    canRelease && 'Release builds to users',
    canManageUsers && 'Invite people and change their permissions',
    canManageBilling && 'Manage billing',
    signing &&
      canManageSigning &&
      'Manage signing certificates and keys, and the apps that use them',
    canManageSso && 'Set up single sign-on and the team’s sign-in policy',
  ].filter((line): line is string => Boolean(line));
};
